/**
 * The shop's side of the consent banner igive.no serves.
 *
 * The banner, its wording, the stored record and the question of which tools
 * a yes covers all belong to the hosted script, https://igive.no/consent/v1.js,
 * which ConsentUiService loads. The shop never reads or writes the record
 * itself. What it keeps is what it loads on an answer and what it deletes
 * after a no: the gate in src/index.html for the GTM container,
 * MetaPixelService and GoogleAdsService for the marketing tools, and
 * ConsentUiService for TikTok's cookies, which igive.no writes.
 *
 * Fail-closed guarantee: until the script has run, or when it never runs,
 * consentCovers() is false for every tool, so nothing loads.
 */

export const CONSENT_CATEGORIES = ['statistikk', 'markedsforing'] as const;

/**
 * Announced by the hosted script on window whenever the banner or its reload
 * offer opens or closes, with `detail: { open: boolean }`. It fires while the
 * script runs if the banner opens on load, so listen before inserting it.
 */
export const CONSENT_UI_EVENT = 'igive:consent-ui';

/**
 * Cookie name prefixes the gate expires when `statistikk` is withdrawn: GA4's
 * own (_ga, _ga_<id>, _gid, _gat*) plus GTM container-specific ones. Google
 * Ads' _gcl_* are not among them: GoogleAdsService.revoke() expires those,
 * with the choice they belong to. Declared here purely so the parity spec can
 * hold the gate's hardcoded copy to it. Nothing in the Angular half reads
 * them: the deletion has to happen in the gate, next to the injection it undoes.
 */
export const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_dc_gtm_'] as const;

/**
 * Cookie name prefixes TikTok's pixel writes on .igive.no: _ttp and
 * _tt_enable_cookie, ttcsid and ttcsid_<pixel id>, and ttclid after an ad
 * click. The pixel runs on igive.no, never in the shop, but the shared domain
 * puts its cookies here too, so a marketing no given in the shop expires them
 * as igive.no's own pages do. Kept identical to igive.no's TIKTOK_COOKIE_PREFIXES.
 */
export const TIKTOK_COOKIE_PREFIXES = ['_ttp', '_tt_', 'ttclid', 'ttcsid'] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/** The tools the hosted banner names; covers() answers for each. */
export type ConsentTool = 'googleAnalytics' | 'meta' | 'googleAds' | 'sleeknote';

/** What window.igiveConsent.get() returns. `partial` has never been asked about marketing. */
export type ConsentState =
  | { status: 'unknown' }
  | { status: 'decided'; scope: ConsentCategory[]; ts: number; v: number }
  | { status: 'partial'; scope: ConsentCategory[]; ts: number };

export interface ConsentGateError {
  error: unknown;
  tag: string;
}

/** The hosted script's page API (igive-no src/consent/boot.ts). */
export interface IgiveConsent {
  get: () => ConsentState;
  covers: (tool: ConsentTool) => boolean;
  reopen: (trigger?: HTMLElement | null) => void;
}

/** The page's half of the contract: the script calls these, never the shop. */
export interface ConsentGate {
  apply: (scope: readonly ConsentCategory[]) => void;
  /** True when only a reload can finish the withdrawal; the banner then offers one. */
  revoke: (scope: readonly ConsentCategory[]) => boolean;
}

declare global {
  interface Window {
    /** Exists once the hosted script has run. */
    igiveConsent?: IgiveConsent;
    /** Defined by src/index.html, wrapped by ConsentUiService before the script loads. */
    __igiveConsentGate?: ConsentGate;
    /**
     * Sink for failures both halves would otherwise swallow in silence.
     *
     * Starts life as a plain array created by whichever half runs first — the
     * gate in index.html cannot import this module, so it builds the same
     * array by hand. main.ts drains it once Sentry has initialised and then
     * swaps in a live reporter, which is why this is typed by its `push` rather
     * than as an array: after the swap it is no longer one.
     */
    __igiveConsentGateErrors?: { push: (entry: ConsentGateError) => unknown };
  }
}

/**
 * Whether the stored answer lets `tool` load, as the hosted script decides it:
 * Meta on any marketing yes, a tool the banner started naming later only on a
 * marketing yes given since. False before the script runs.
 */
export function consentCovers(tool: ConsentTool): boolean {
  try {
    return window.igiveConsent?.covers(tool) === true;
  } catch {
    return false;
  }
}

/**
 * Bounded so the buffer cannot grow without limit where nothing drains it.
 * Sentry is DSN-gated to production, so on staging and in dev this array is
 * written and never read.
 */
export const CONSENT_ERROR_BUFFER_LIMIT = 20;

/**
 * Records a swallowed failure for Sentry to pick up later.
 *
 * Deliberately not a direct Sentry call: this module must stay free of
 * framework and vendor dependencies, and the gate half runs before Sentry.init
 * exists at all. Buffering is the only mechanism that works for both.
 */
export function reportConsentError(error: unknown, tag: string): void {
  try {
    const sink = window.__igiveConsentGateErrors ?? (window.__igiveConsentGateErrors = []);
    if (Array.isArray(sink) && sink.length >= CONSENT_ERROR_BUFFER_LIMIT) return;
    sink.push({ error, tag });
  } catch {
    // A reporter that throws must never become the failure it is reporting.
  }
}

/**
 * Expires every cookie whose name starts with one of `prefixes`, on the host
 * and on each parent domain up to, never including, the public suffix. A
 * third-party tool sets its cookies on the registrable domain, and a cookie
 * can only be deleted with a matching Domain attribute, so every candidate
 * domain is tried. The Angular-side counterpart of clearAnalyticsCookies() in
 * the src/index.html gate, which cannot import this module.
 */
export function expireCookiesByPrefix(prefixes: readonly string[]): void {
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => prefixes.some((prefix) => name.startsWith(prefix)));

  // '' means no Domain attribute, which is how a host-only cookie is deleted.
  // The loop stops one short of the last label so it never aims at 'no'.
  const parts = window.location.hostname.split('.');
  const domains = [''];
  for (let p = 0; p < parts.length - 1; p++) {
    domains.push(`.${parts.slice(p).join('.')}`);
  }

  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Path=/; Max-Age=0${domain ? `; Domain=${domain}` : ''}`;
    }
  }
}
